import type {
    BaseQueryApi,
    FetchArgs,
    FetchBaseQueryError,
    FetchBaseQueryMeta
} from '@reduxjs/toolkit/query/react';
import { fetchBaseQuery } from '@reduxjs/toolkit/query';
import { isServer } from 'tradera/utils/nextjs';
import type { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import type { ResponseStatus } from 'tradera/state/services/types/webapi-auth-generated';
import { logger } from 'packages/logger';
import { refreshTokensOnce } from 'tradera/utils/http/api-renew-tokens';

const throwIfServer = () => {
    if (isServer) {
        throw new Error(
            'There are module scope variables in this module and is for client side use only!!!'
        );
    }
};

const jsonRequestHeaders: Record<string, string> = {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
};

type ErrorResponseData = {
    responseStatus: ResponseStatus;
};

type ResponseType = QueryReturnValue<
    unknown,
    FetchBaseQueryError,
    FetchBaseQueryMeta
>;

const isMissingAuthToken = (response: ResponseType) => {
    const errorData = response.error?.data as ErrorResponseData | undefined;
    return (
        response.error &&
        response.error.status === 400 &&
        errorData?.responseStatus.errorCode === 'MissingAuthToken'
    );
};

const isInvalidOrExpiredAuthToken = (response: ResponseType) => {
    const errorData = response.error?.data as ErrorResponseData | undefined;
    return (
        response.error &&
        response.error.status === 401 &&
        errorData?.responseStatus?.errorCode === 'InvalidOrExpiredAuthToken'
    );
};

const baseQuery = fetchBaseQuery({
    baseUrl: '',
    prepareHeaders: (headers) => {
        Object.keys(jsonRequestHeaders).forEach((headerName) => {
            headers.set(headerName, jsonRequestHeaders[headerName]);
        });
        return headers;
    },
    credentials: 'include'
});

const frontendBaseQueryWebApiReauth = async (
    args: string | FetchArgs,
    api: BaseQueryApi,
    extraOptions: object
): Promise<QueryReturnValue> => {
    throwIfServer();

    const originalBaseQuery = () => baseQuery(args, api, extraOptions);
    let result = await originalBaseQuery();

    if (isMissingAuthToken(result) || isInvalidOrExpiredAuthToken(result)) {
        await refreshTokensOnce();
        // Re-run original query with fresh tokens
        result = await originalBaseQuery();
    }

    return result;
};

export const frontendBaseQueryWithReauth = async (
    input: string | FetchArgs,
    api: BaseQueryApi,
    extraOptions: object
): Promise<QueryReturnValue> => {
    throwIfServer();
    try {
        return frontendBaseQueryWebApiReauth(input, api, extraOptions);
    } catch (error) {
        logger(error);
        // Base query and query functions must always catch errors themselves, and return it in an object!
        // https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#implementing-a-custom-basequery
        return {
            error
        };
    }
};
